/**
 * Rider · Account & Settings
 *
 * Mirrors the Customer ProfilePage / ProfileEditPage but trimmed for
 * the Rider PWA scope:
 *
 *   • Profile summary (read-only name + mobile)
 *   • Earnings link
 *   • Order history link
 *   • Terms & Conditions
 *   • Privacy Policy
 *   • Delete account (OTP-gated, guarded by COD-held & active-orders)
 *   • Logout
 *
 * App Store / Play Store policies require self-serve deletion for any
 * account-creating app — this page satisfies that requirement for the
 * Rider role. The backend enforces additional safety gates so a rider
 * with unsettled COD cash or active deliveries cannot silently wipe
 * their audit trail.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ChevronRight, Wallet, History, FileText, ShieldCheck, Trash2,
    AlertTriangle, Loader2, LogOut, Bike, ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import NotificationPermissionCard from "../../components/NotificationPermissionCard";


export default function RiderAccount() {
    const { user, logout } = useAuth();
    const nav = useNavigate();

    // Account-deletion two-step modal (mirrors customer flow).
    const [del, setDel] = useState(null);            // null | { stage: "confirm" } | { stage: "otp" }
    const [otp, setOtp] = useState("");
    const [delBusy, setDelBusy] = useState(false);

    const askDeleteOtp = async () => {
        setDelBusy(true);
        try {
            await api.post("/auth/otp/request", {
                mobile_number: user.mobile_number,
                role: user.role,
                channel: "SMS",
            });
            toast.success("OTP sent — enter it to confirm deletion");
            setDel({ stage: "otp" });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to send OTP");
        } finally { setDelBusy(false); }
    };

    const confirmDelete = async () => {
        if (otp.length !== 6) { toast.error("Enter the 6-digit OTP"); return; }
        setDelBusy(true);
        try {
            await api.post("/auth/me/delete", { otp });
            toast.success("Account deleted. Safe travels!");
            await logout();
            nav("/login");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        } finally { setDelBusy(false); }
    };

    if (!user) return null;

    const menu = [
        { Icon: Wallet, label: "Earnings & Ledger", to: "/rider/earnings", testid: "rider-menu-earnings" },
        { Icon: History, label: "Ride history", to: "/rider/history", testid: "rider-menu-history" },
        { Icon: FileText, label: "Terms & Conditions", to: "/legal/terms", testid: "rider-menu-terms" },
        { Icon: ShieldCheck, label: "Privacy Policy", to: "/legal/privacy", testid: "rider-menu-privacy" },
    ];

    return (
        <div className="pb-32 max-w-xl mx-auto" data-testid="rider-account-page">
            <header className="px-4 pt-5 pb-4 flex items-center gap-3 bg-card border-b border-border sticky top-0 z-10">
                <Link to="/rider" className="p-1 -ml-1" data-testid="rider-account-back" aria-label="Back">
                    <ArrowLeft size={22} />
                </Link>
                <h1 className="text-lg font-bold flex-1">Account</h1>
            </header>

            <section className="m-4 bg-card rounded-2xl border border-border p-5 flex items-center gap-4" data-testid="rider-account-card">
                <div className="w-14 h-14 rounded-full bg-violet-100 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 flex items-center justify-center">
                    <Bike size={26} />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-bold text-base truncate" data-testid="rider-account-name">
                        {user.first_name || user.name || "Rider"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5" data-testid="rider-account-mobile">
                        +91 {user.mobile_number}
                    </p>
                </div>
            </section>

            {/* iter134t — push permission control (renders on native only). */}
            <section className="mx-4">
                <NotificationPermissionCard />
            </section>

            <nav className="m-4 bg-card rounded-2xl border border-border divide-y divide-border overflow-hidden">
                {menu.map(({ Icon, label, to, testid }) => (
                    <Link
                        key={label}
                        to={to}
                        data-testid={testid}
                        className="flex items-center gap-4 px-4 py-4 hover:bg-muted/40 transition"
                    >
                        <Icon size={20} className="text-muted-foreground" />
                        <p className="flex-1 font-semibold text-sm">{label}</p>
                        <ChevronRight size={18} className="text-muted-foreground" />
                    </Link>
                ))}
            </nav>

            <section
                className="m-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-2xl p-5"
                data-testid="rider-danger-zone"
            >
                <p className="text-[11px] uppercase tracking-wider font-bold text-red-700 dark:text-red-300 inline-flex items-center gap-1.5">
                    <AlertTriangle size={12} /> Danger zone
                </p>
                <h3 className="font-bold mt-1">Delete my account</h3>
                <p className="text-xs text-muted-foreground mt-1">
                    This permanently removes your rider profile. <b>Settle any COD cash you&apos;re holding</b> and
                    complete or hand off any active deliveries before deleting.
                </p>
                <button
                    onClick={() => setDel({ stage: "confirm" })}
                    data-testid="rider-delete-btn"
                    className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-xs font-bold"
                >
                    <Trash2 size={13} /> Delete account
                </button>
            </section>

            <button
                data-testid="rider-logout-btn"
                onClick={async () => { try { await logout(); } finally { nav("/login"); } }}
                className="mx-4 mt-2 w-[calc(100%-2rem)] bg-card border border-border rounded-2xl py-3 inline-flex items-center justify-center gap-2 font-semibold text-destructive"
            >
                <LogOut size={18} /> Logout
            </button>

            {del && (
                <div
                    className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
                    onClick={() => !delBusy && setDel(null)}
                >
                    <div
                        className="bg-card rounded-3xl p-6 w-full max-w-sm shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="rider-delete-modal"
                    >
                        {del.stage === "confirm" ? (
                            <>
                                <h2 className="text-lg font-bold">Are you sure?</h2>
                                <p className="text-sm text-muted-foreground mt-2">
                                    We&apos;ll send a one-time code to <b>+91 {user.mobile_number}</b> to confirm.
                                    Once deleted, this cannot be undone.
                                </p>
                                <div className="flex gap-2 mt-5">
                                    <button
                                        onClick={() => setDel(null)}
                                        className="flex-1 rounded-xl bg-muted py-2.5 text-sm font-semibold"
                                        data-testid="rider-delete-cancel"
                                    >Cancel</button>
                                    <button
                                        onClick={askDeleteOtp}
                                        disabled={delBusy}
                                        className="flex-1 rounded-xl bg-red-600 text-white py-2.5 text-sm font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
                                        data-testid="rider-delete-send-otp"
                                    >
                                        {delBusy && <Loader2 size={13} className="animate-spin" />} Send OTP
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <h2 className="text-lg font-bold">Confirm deletion</h2>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Enter the 6-digit code we sent to <b>+91 {user.mobile_number}</b>.
                                </p>
                                <input
                                    data-testid="rider-delete-otp"
                                    inputMode="numeric"
                                    maxLength={6}
                                    placeholder="6-digit code"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                                    className="mt-4 w-full bg-muted rounded-xl px-4 py-3 text-lg tracking-[0.4em] text-center font-bold outline-none"
                                />
                                <div className="flex gap-2 mt-5">
                                    <button
                                        onClick={() => setDel(null)}
                                        className="flex-1 rounded-xl bg-muted py-2.5 text-sm font-semibold"
                                        data-testid="rider-delete-cancel-otp"
                                    >Cancel</button>
                                    <button
                                        onClick={confirmDelete}
                                        disabled={delBusy}
                                        className="flex-1 rounded-xl bg-red-600 text-white py-2.5 text-sm font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
                                        data-testid="rider-delete-confirm"
                                    >
                                        {delBusy && <Loader2 size={13} className="animate-spin" />} Delete forever
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
